import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../HardwareFirstQuiz.module.scss';
import { QuizAnswers, QuizOption, STEPS } from '../steps';

/** How long each answer takes to tick over, and the pause before the result. */
const TICK_MS = 320;
const SETTLE_MS = 400;
/** The figma shows seven rows; more than that overflows the sheet. */
const MAX_ROWS = 7;

interface CalculatingStepProps {
  answers: QuizAnswers;
  onDone(): void;
}

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8.4 6.3 11.2 12.5 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** The visitor's own picks, in the order the quiz asked for them. */
function answerLabelKeys(answers: QuizAnswers): string[] {
  const keys: string[] = [];

  STEPS.forEach((step) => {
    if (step.kind === 'email') {
      return;
    }
    // The step union gives `options` a union of readonly arrays, which has no
    // callable `find`; every member is a QuizOption, so widen it once.
    const options = step.options as readonly QuizOption<string>[];
    const picked = answers[step.id as 'age' | 'size' | 'residence' | 'exercise' | 'importance'];
    const pickedIds = Array.isArray(picked) ? picked : [picked];

    pickedIds.forEach((id) => {
      const option = options.find((o) => o.id === id);
      if (option) {
        keys.push(option.labelKey);
      }
    });
  });

  return keys.slice(0, MAX_ROWS);
}

/**
 * Theatre, not work: the recommendation is already decided by the time this
 * renders. It plays the visitor's answers back so the result does not arrive
 * instantly after six questions.
 *
 * The bar is honest about that theatre rather than a spinner pretending to
 * wait on something: the whole run is `rows * TICK_MS + SETTLE_MS`, so it can
 * show real progress against a known end, and the sheet gets a beat that ends
 * when it says it will.
 */
export default function CalculatingStep({ answers, onDone }: CalculatingStepProps) {
  const { t } = useTranslation();
  const labelKeys = useMemo(() => answerLabelKeys(answers), [answers]);
  const [checked, setChecked] = useState(0);

  useEffect(() => {
    if (checked >= labelKeys.length) {
      const done = setTimeout(onDone, SETTLE_MS);
      return () => clearTimeout(done);
    }
    const tick = setTimeout(() => setChecked((n) => n + 1), TICK_MS);
    return () => clearTimeout(tick);
  }, [checked, labelKeys.length, onDone]);

  // counts the settle as one more beat, so the bar reaches full as the result lands
  const progress = labelKeys.length ? checked / labelKeys.length : 1;

  return (
    <div className={classNames(styles.stepBody, styles.calculating)}>
      <h2 className={styles.stepTitle}>{t('hf_quiz_calculating')}</h2>

      <div className={styles.calculatingTrack}>
        <span className={styles.calculatingFill} style={{ transform: `scaleX(${progress})` }} />
      </div>

      <ul className={styles.calculatingList}>
        {labelKeys.map((key, i) => (
          <li
            key={key}
            className={classNames(styles.calculatingRow, {
              [styles.calculatingRowDone]: i < checked,
              [styles.calculatingRowActive]: i === checked,
            })}
            // each row settles in just before its turn, so the list builds rather
            // than sitting there greyed out from the start
            style={{ animationDelay: `${i * TICK_MS}ms` }}
          >
            <span className={styles.calculatingMark}>
              <CheckIcon />
            </span>
            <span className={styles.calculatingLabel}>{t(key)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
